/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Alert } from 'react-bootstrap';
import './DeviceMapCanvas.scss';

// Use "globalThis" instead of "window", "self", or "global" (javascript:S7764)
const window = globalThis.window;
const GOOGLE_MAPS_API_KEY = process.env.GOOGLE_MAPS_API_KEY || '';
const GOOGLE_MAPS_MAP_ID = process.env.GOOGLE_MAPS_MAP_ID || '';
const STATUS_COLORS = {
  online: '#2ecc71',
  warning: '#f1c40f',
  offline: '#e74c3c',
  unknown: '#95a5a6',
};
const STATUS_LABELS = {
  online: 'Online',
  warning: 'Warning',
  offline: 'Offline',
  unknown: 'Unknown',
};
const CONTENT_COLORS = {
  current: '#2ecc71',
  downloading: '#3498db',
  pending: '#f39c12',
  outdated: '#e74c3c',
  unknown: '#95a5a6',
};
const CONTENT_LABELS = {
  current: 'Current',
  downloading: 'Downloading',
  pending: 'Pending',
  outdated: 'Outdated',
  unknown: 'Unknown',
};
const STATUS_KEYS = ['offline', 'warning', 'online', 'unknown'];
const CONTENT_KEYS = ['outdated', 'pending', 'downloading', 'current', 'unknown'];

export const parseNumber = value => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const num = typeof value === 'number' ? value : Number.parseFloat(value);

  return Number.isFinite(num) ? num : null;
};

export const pointsToRows = points =>
  (points || [])
    .map(point => {
      const lat = parseNumber(point.geo_lat);
      const lng = parseNumber(point.geo_lng);

      if (lat === null || lng === null) {
        return null;
      }

      return {
        ...point,
        geo_lat: lat,
        geo_lng: lng,
      };
    })
    .filter(Boolean);

export const getMarkerColor = (row, colorMode) => {
  if (colorMode === 'content') {
    return CONTENT_COLORS[row.download_status] || CONTENT_COLORS.unknown;
  }

  return STATUS_COLORS[row.online_status] || STATUS_COLORS.unknown;
};

export const getClusterBreakdown = (markers, colorMode) => {
  const palette = colorMode === 'content' ? CONTENT_COLORS : STATUS_COLORS;
  const keys = colorMode === 'content' ? CONTENT_KEYS : STATUS_KEYS;
  const counts = keys.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});

  markers.forEach(marker => {
    const row = marker.__row;

    if (!row) {
      counts.unknown += 1;

      return;
    }

    const key = colorMode === 'content' ? row.download_status : row.online_status;
    const normalized = keys.includes(key) ? key : 'unknown';

    counts[normalized] += 1;
  });

  const total = Object.values(counts).reduce((sum, value) => sum + value, 0);

  return { counts, keys, palette, total };
};

export const getStatusCounts = rows =>
  rows.reduce(
    (accumulator, row) => {
      const online = row.online_status || 'offline';
      const download = row.download_status || 'outdated';

      if (accumulator.onlineStatus[online] !== undefined) {
        accumulator.onlineStatus[online] += 1;
      }

      if (accumulator.contentStatus[download] !== undefined) {
        accumulator.contentStatus[download] += 1;
      }

      return accumulator;
    },
    {
      onlineStatus: { online: 0, warning: 0, offline: 0 },
      contentStatus: { current: 0, downloading: 0, pending: 0, outdated: 0 },
    },
  );

export const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

export const describeDonutSegment = (x, y, outerRadius, innerRadius, startAngle, endAngle) => {
  const startOuter = polarToCartesian(x, y, outerRadius, endAngle);
  const endOuter = polarToCartesian(x, y, outerRadius, startAngle);
  const startInner = polarToCartesian(x, y, innerRadius, startAngle);
  const endInner = polarToCartesian(x, y, innerRadius, endAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

  return [
    `M ${startOuter.x} ${startOuter.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 0 ${endOuter.x} ${endOuter.y}`,
    `L ${startInner.x} ${startInner.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 1 ${endInner.x} ${endInner.y}`,
    'Z',
  ].join(' ');
};

export const createClusterContent = ({ count, breakdown }) => {
  const getSize = c => {
    if (c < 10) {
      return 36;
    }

    if (c < 100) {
      return 44;
    }

    return 52;
  };

  const size = getSize(count);
  const radius = size / 2;
  const innerRadius = radius * 0.58;
  const { counts, keys, palette, total } = breakdown;
  const el = document.createElement('div');
  const segments = keys
    .filter(key => counts[key] > 0)
    .map(key => ({ key, value: counts[key], color: palette[key] || palette.unknown }));
  const totalCount = total || count;
  let currentAngle = 0;
  const paths = [];

  if (!segments.length) {
    segments.push({ key: 'unknown', value: totalCount, color: palette.unknown });
  }

  if (segments.length === 1) {
    paths.push(
      `<circle cx="${radius}" cy="${radius}" r="${radius}" fill="${segments[0].color}" />`,
    );
  } else {
    segments.forEach(segment => {
      const sliceAngle = (segment.value / totalCount) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + sliceAngle;
      const d = describeDonutSegment(radius, radius, radius, innerRadius, startAngle, endAngle);

      paths.push(`<path d="${d}" fill="${segment.color}" />`);
      currentAngle = endAngle;
    });
  }

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      ${paths.join('')}
      <circle cx="${radius}" cy="${radius}" r="${innerRadius}" fill="#ffffff" />
      <text x="${radius}" y="${radius}" text-anchor="middle" dominant-baseline="central"
        fill="#333" font-size="${count < 100 ? 13 : 14}" font-weight="600">${count}</text>
    </svg>
  `;

  el.innerHTML = svg.trim();

  return el;
};

export const escapeHtml = value =>
  String(value).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

export const buildMarkerInfoRows = row => {
  const rows = [];

  const addRow = (label, value, valueHtml = null) => {
    if (!value) {
      return;
    }

    rows.push({ label, value, valueHtml });
  };

  addRow('Address', row.geo_geocode_address && escapeHtml(row.geo_geocode_address));

  if (row.geo_country_code || row.geo_postal_code) {
    const country = row.geo_country_code ? escapeHtml(row.geo_country_code) : '';
    const zip = row.geo_postal_code ? escapeHtml(row.geo_postal_code) : '';
    const spacer = country && zip ? ' ' : '';

    addRow('Postal', `${country}${spacer}${zip}`.trim());
  }

  if (row.geo_address_line) {
    addRow('Input', escapeHtml(row.geo_address_line));
  }

  if (row.online_status) {
    addRow('Status', escapeHtml(row.online_status));
  }

  if (row.download_status) {
    addRow('Content', escapeHtml(row.download_status));
  }

  const deviceId = escapeHtml(row.id);
  const deviceId36 = escapeHtml(row.device_id36 || row.name || '');

  if (deviceId36 && deviceId) {
    const linkHtml = `<a class="device-map-tooltip__link" href="/devices/${deviceId}" target="_blank" rel="noopener noreferrer">${deviceId36}</a>`;

    addRow('Device ID (36)', deviceId36, linkHtml);
  } else if (deviceId36) {
    addRow('Device ID (36)', deviceId36);
  }

  addRow('Device ID', escapeHtml(row.id));

  return rows;
};

let googleMapsPromise = null;
const GOOGLE_MAPS_CALLBACK = '__directorGoogleMapsInit';

const waitForGoogleMaps = (resolve, reject, attempts = 0) => {
  if (window.google?.maps?.Map) {
    resolve(window.google);

    return;
  }

  if (attempts > 60) {
    reject(new Error('Google Maps did not finish initializing.'));

    return;
  }

  setTimeout(() => waitForGoogleMaps(resolve, reject, attempts + 1), 50);
};

const loadGoogleMaps = () => {
  if (window.google?.maps?.Map) {
    return Promise.resolve(window.google);
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return Promise.reject(new Error('Missing GOOGLE_MAPS_API_KEY.'));
  }

  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById('google-maps-js');

    if (existing) {
      if (window.google?.maps?.Map) {
        resolve(window.google);

        return;
      }

      existing.addEventListener('load', () => waitForGoogleMaps(resolve, reject));
      existing.addEventListener('error', reject);

      return;
    }

    const script = document.createElement('script');

    window[GOOGLE_MAPS_CALLBACK] = () => waitForGoogleMaps(resolve, reject);

    script.id = 'google-maps-js';
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      GOOGLE_MAPS_API_KEY,
    )}&loading=async&libraries=marker&callback=${GOOGLE_MAPS_CALLBACK}`;
    script.onload = () => waitForGoogleMaps(resolve, reject);
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return googleMapsPromise;
};

export default class DeviceMapCanvas extends Component {
  state = {
    mapReady: false,
    mapError: null,
    dataRows: [],
    dataError: null,
    totalRows: [],
  };

  mapRef = React.createRef();

  map = null;

  markers = [];

  clusterer = null;

  overlay = null;

  mapMoveTimeout = null;

  resizeObserver = null;

  resizeTimer = null;

  pollTimer = null;

  totalPollTimer = null;

  fetchController = null;

  totalFetchController = null;

  lastFetchAt = 0;

  hoverCloseTimeout = null;

  hasFitBounds = false;

  ignoreNextIdle = false;

  lastBoundsKey = null;

  componentDidMount() {
    this.initMap();
    this.setupResizeObserver();
    document.addEventListener('visibilitychange', this.handleVisibility);
    window.addEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps, prevState) {
    const prevRows = this.getRows(prevProps, prevState);
    const nextRows = this.getRows(this.props, this.state);
    const { mapError } = this.state;

    if (!this.map && this.mapRef.current && !mapError) {
      this.initMap();
    }

    if (!this.resizeObserver && this.mapRef.current) {
      this.setupResizeObserver();
    }

    const { colorMode } = this.props;

    if (prevRows !== nextRows || prevProps.colorMode !== colorMode) {
      this.refreshMarkers(nextRows);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.mapMoveTimeout);
    clearTimeout(this.hoverCloseTimeout);
    clearTimeout(this.resizeTimer);
    clearInterval(this.pollTimer);
    clearInterval(this.totalPollTimer);

    if (this.fetchController) {
      this.fetchController.abort();
      this.fetchController = null;
    }

    if (this.totalFetchController) {
      this.totalFetchController.abort();
      this.totalFetchController = null;
    }

    if (this.clusterer) {
      this.clusterer.clearMarkers();
      this.clusterer.setMap(null);
      this.clusterer = null;
    }

    this.markers.forEach(marker => this.detachMarker(marker));
    this.markers = [];

    if (this.overlay) {
      this.overlay.setMap(null);
      this.overlay = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.map && window.google?.maps) {
      window.google.maps.event.clearInstanceListeners(this.map);
    }

    this.map = null;
  }

  getRows = (props, state) => {
    const resolvedState = state || this.state;

    if (props.rows) {
      return props.rows;
    }

    if (props.points) {
      return pointsToRows(props.points);
    }

    return resolvedState?.dataRows || [];
  };

  setMapStatus = nextState => {
    this.setState(nextState);

    if (this.props.onMapStatus) {
      this.props.onMapStatus(nextState);
    }
  };

  handleVisibility = () => {
    if (this.props.pauseWhenHidden !== false && document.visibilityState === 'hidden') {
      return;
    }

    if (document.visibilityState === 'hidden') {
      return;
    }

    if (this.props.dataUrl) {
      this.fetchData();
    }

    this.requestBounds();
  };

  setupResizeObserver = () => {
    if (this.resizeObserver || !this.mapRef.current) {
      return;
    }

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.mapRef.current);
  };

  handleResize = () => {
    if (!this.map || !window.google?.maps) {
      return;
    }

    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      if (!this.map || !window.google?.maps) {
        return;
      }

      window.google.maps.event.trigger(this.map, 'resize');

      if (this.overlay?.draw) {
        this.overlay.draw();
      }
    }, 50);
  };

  initMap = () => {
    if (this.map || !this.mapRef.current) {
      return;
    }

    if (!GOOGLE_MAPS_MAP_ID) {
      if (!this.state.mapError) {
        this.setMapStatus({ mapError: 'Missing GOOGLE_MAPS_MAP_ID.' });
      }

      return;
    }

    window.gm_authFailure = () => {
      this.setMapStatus({
        mapError: 'Google Maps auth failure. Check API key restrictions/billing.',
      });
    };

    loadGoogleMaps()
      .then(google => {
        if (!this.mapRef.current) {
          return;
        }

        this.map = new google.maps.Map(this.mapRef.current, {
          center: { lat: 20, lng: 0 },
          zoom: 2,
          mapId: GOOGLE_MAPS_MAP_ID,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });

        google.maps.event.addListenerOnce(this.map, 'tilesloaded', () => {
          this.setMapStatus({ mapReady: true });
        });
        google.maps.event.addListenerOnce(this.map, 'idle', () => {
          this.setMapStatus({ mapReady: true });
        });

        setTimeout(() => {
          if (!this.map || !window.google?.maps) {
            return;
          }

          window.google.maps.event.trigger(this.map, 'resize');
          this.map.setCenter({ lat: 20, lng: 0 });
        }, 0);

        this.map.addListener('idle', this.handleMapIdle);
        this.ensureOverlay();
        this.refreshMarkers(this.getRows(this.props, this.state));
        this.requestBounds();
        this.setupResizeObserver();
        this.handleResize();

        if (this.props.dataUrl) {
          this.startPolling();
          this.fetchData();

          if (this.props.showSummary || this.props.onTotals) {
            this.startTotalPolling();
            this.fetchTotals();
          }
        }
      })
      .catch(err => {
        this.setMapStatus({ mapError: err.message || 'Failed to load Google Maps.' });
      });
  };

  handleMapIdle = () => {
    if (this.ignoreNextIdle) {
      this.ignoreNextIdle = false;

      return;
    }

    clearTimeout(this.mapMoveTimeout);
    this.mapMoveTimeout = setTimeout(() => {
      this.requestBounds();

      if (this.props.dataUrl) {
        this.fetchData();
      }
    }, 250);
  };

  requestBounds = () => {
    const { onBoundsChange } = this.props;

    if (!this.map || !onBoundsChange) {
      return;
    }

    const bounds = this.map.getBounds();

    if (!bounds) {
      onBoundsChange();

      return;
    }

    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    const minLat = southWest.lat();
    const maxLat = northEast.lat();
    const minLng = southWest.lng();
    const maxLng = northEast.lng();
    const key = `${minLat.toFixed(4)}-${maxLat.toFixed(4)}-${minLng.toFixed(4)}-${maxLng.toFixed(4)}`;

    if (this.lastBoundsKey === key) {
      return;
    }

    this.lastBoundsKey = key;
    onBoundsChange({ min_lat: minLat, max_lat: maxLat, min_lng: minLng, max_lng: maxLng });
  };

  startPolling = () => {
    const interval = Number.isFinite(this.props.pollIntervalMs) ? this.props.pollIntervalMs : 15000;

    clearInterval(this.pollTimer);

    if (interval <= 0) {
      return;
    }

    this.pollTimer = setInterval(() => {
      if (this.props.pauseWhenHidden !== false && document.visibilityState === 'hidden') {
        return;
      }

      this.fetchData();
    }, interval);
  };

  startTotalPolling = () => {
    const interval = Number.isFinite(this.props.totalPollIntervalMs)
      ? this.props.totalPollIntervalMs
      : this.props.pollIntervalMs || 15000;

    clearInterval(this.totalPollTimer);

    if (interval <= 0) {
      return;
    }

    this.totalPollTimer = setInterval(() => {
      if (this.props.pauseWhenHidden !== false && document.visibilityState === 'hidden') {
        return;
      }

      this.fetchTotals();
    }, interval);
  };

  getBoundsParams = () => {
    if (!this.map) {
      return null;
    }

    const bounds = this.map.getBounds();

    if (!bounds) {
      return null;
    }

    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();

    return {
      min_lat: southWest.lat(),
      max_lat: northEast.lat(),
      min_lng: southWest.lng(),
      max_lng: northEast.lng(),
    };
  };

  fetchData = () => {
    const { dataUrl, onData } = this.props;

    if (!dataUrl) {
      return;
    }

    const bounds = this.getBoundsParams();
    const query = bounds
      ? `?min_lat=${encodeURIComponent(bounds.min_lat)}&max_lat=${encodeURIComponent(
          bounds.max_lat,
        )}&min_lng=${encodeURIComponent(bounds.min_lng)}&max_lng=${encodeURIComponent(bounds.max_lng)}`
      : '';

    if (this.fetchController) {
      this.fetchController.abort();
    }

    this.fetchController = new AbortController();

    fetch(`${dataUrl}${query}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal: this.fetchController.signal,
    })
      .then(res => {
        if (!res.ok) {
          const err = new Error(`Map fetch failed (${res.status})`);

          err.status = res.status;
          throw err;
        }

        return res.json();
      })
      .then(data => {
        const rows = pointsToRows(data.points || []);

        this.setState({ dataRows: rows, dataError: null });

        if (onData) {
          onData(rows);
        }
      })
      .catch(err => {
        if (err.name === 'AbortError') {
          return;
        }

        this.setState({ dataError: err.message || 'Failed to fetch map data.' });
      });
  };

  fetchTotals = () => {
    const { dataUrl, onTotals } = this.props;

    if (!dataUrl) {
      return;
    }

    if (this.totalFetchController) {
      this.totalFetchController.abort();
    }

    this.totalFetchController = new AbortController();

    fetch(dataUrl, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal: this.totalFetchController.signal,
    })
      .then(res => {
        if (!res.ok) {
          const err = new Error(`Map fetch failed (${res.status})`);

          err.status = res.status;
          throw err;
        }

        return res.json();
      })
      .then(data => {
        const rows = pointsToRows(data.points || []);

        this.setState({ totalRows: rows });

        if (onTotals) {
          onTotals(rows);
        }
      })
      .catch(err => {
        if (err.name === 'AbortError') {
          return;
        }

        this.setState({ dataError: err.message || 'Failed to fetch map data.' });
      });
  };

  ensureOverlay = () => {
    if (this.overlay || !this.map || !window.google?.maps) {
      return;
    }

    const { google } = window;

    class HoverInfoOverlay extends google.maps.OverlayView {
      constructor(map, onEnter, onLeave) {
        super();
        this.map = map;
        this.onEnter = onEnter;
        this.onLeave = onLeave;
        this.container = null;
        this.position = null;
        this.pending = null;
        this.mapDiv = map?.getDiv?.() || null;
        this.anchorEl = null;
        this.setMap(map);
      }

      onAdd() {
        this.container = document.createElement('div');
        this.container.className = 'device-map-tooltip';
        this.container.style.display = 'none';
        this.container.style.position = 'absolute';
        this.container.style.pointerEvents = 'auto';
        this.container.style.zIndex = '1000';
        this.container.addEventListener('mouseenter', this.onEnter);
        this.container.addEventListener('mouseleave', this.onLeave);
        const panes = this.getPanes();

        if (!this.mapDiv) {
          this.mapDiv = this.map?.getDiv?.() || null;
        }

        if (this.mapDiv) {
          this.mapDiv.appendChild(this.container);
        } else {
          (panes.floatPane || panes.overlayMouseTarget).appendChild(this.container);
        }

        if (this.pending) {
          this.open(this.pending.position, this.pending.html);
        }
      }

      draw() {
        if (!this.container || !this.position) {
          return;
        }

        let point = null;

        if (this.anchorEl && this.mapDiv) {
          const mapRect = this.mapDiv.getBoundingClientRect();
          const anchorRect = this.anchorEl.getBoundingClientRect();

          point = {
            x: anchorRect.left - mapRect.left + anchorRect.width / 2,
            y: anchorRect.top - mapRect.top,
          };
        } else {
          const projection = this.getProjection();

          if (!projection) {
            setTimeout(() => this.draw(), 50);

            return;
          }

          point = projection.fromLatLngToDivPixel(this.position);
        }

        if (!point) {
          setTimeout(() => this.draw(), 50);

          return;
        }

        const width = this.container.offsetWidth || 0;
        const height = this.container.offsetHeight || 0;
        let left = point.x - width / 2;
        let top = point.y - height - 12;

        if (this.mapDiv) {
          const maxLeft = this.mapDiv.clientWidth - width - 8;
          const maxTop = this.mapDiv.clientHeight - height - 8;

          left = Math.max(8, Math.min(left, maxLeft));
          top = Math.max(8, Math.min(top, maxTop));
        }

        this.container.style.left = `${left}px`;
        this.container.style.top = `${top}px`;
      }

      onRemove() {
        this.container?.remove();

        this.container = null;
      }

      open(position, html, anchorEl = null) {
        this.position = position;
        this.pending = { position, html };
        this.anchorEl = anchorEl;

        if (this.container) {
          this.container.innerHTML = html;
          this.container.style.display = 'block';
          this.container.style.opacity = '1';
          this.container.style.visibility = 'visible';
          this.container.style.pointerEvents = 'auto';
          this.container.style.position = 'absolute';
          this.draw();
        }
      }

      close() {
        if (this.container) {
          this.container.style.display = 'none';
          this.container.style.opacity = '0';
          this.container.style.visibility = 'hidden';
        }

        this.pending = null;
        this.anchorEl = null;
      }
    }

    this.overlay = new HoverInfoOverlay(this.map, this.cancelHoverClose, this.scheduleHoverClose);
  };

  getMarkerLatLng = marker => {
    const { google } = window;

    if (!google?.maps) {
      return null;
    }

    if (marker?.position instanceof google.maps.LatLng) {
      return marker.position;
    }

    if (marker?.position) {
      return new google.maps.LatLng(marker.position);
    }

    if (typeof marker?.getPosition === 'function') {
      return marker.getPosition();
    }

    return null;
  };

  openOverlay = (marker, html, anchorEl) => {
    if (!this.overlay) {
      this.ensureOverlay();
    }

    const position = this.getMarkerLatLng(marker);

    if (!this.overlay || !position) {
      return;
    }

    this.cancelHoverClose();
    this.overlay.open(position, html, anchorEl);
  };

  closeOverlay = () => {
    if (this.overlay) {
      this.overlay.close();
    }
  };

  scheduleHoverClose = () => {
    clearTimeout(this.hoverCloseTimeout);
    this.hoverCloseTimeout = setTimeout(this.closeOverlay, 160);
  };

  cancelHoverClose = () => {
    clearTimeout(this.hoverCloseTimeout);
  };

  bindDomEvents = (target, onOpen) => {
    if (!target || typeof target.addEventListener !== 'function') {
      return;
    }

    if (target.style) {
      target.style.pointerEvents = 'auto';
      target.style.cursor = 'pointer';
    }

    target.addEventListener('mouseenter', () => {
      this.cancelHoverClose();
      onOpen();
    });
    target.addEventListener('mouseleave', () => {
      this.scheduleHoverClose();
    });
    target.addEventListener('click', () => {
      this.cancelHoverClose();
      onOpen();
    });
  };

  detachMarker = marker => {
    if (!marker) {
      return;
    }

    if (typeof marker.setMap === 'function') {
      marker.setMap(null);

      return;
    }

    marker.map = null;
  };

  refreshMarkers = async rows => {
    if (!this.map || !window.google?.maps) {
      return;
    }

    if (this.clusterer) {
      this.clusterer.clearMarkers();
      this.clusterer.setMap(null);
      this.clusterer = null;
    }

    this.markers.forEach(marker => this.detachMarker(marker));
    this.markers = [];
    this.closeOverlay();

    if (!rows.length) {
      return;
    }

    const google = window.google;
    const bounds = new google.maps.LatLngBounds();
    const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary('marker');

    this.markers = rows.map(row => {
      const position = { lat: row.geo_lat, lng: row.geo_lng };

      bounds.extend(position);

      const pin = new PinElement({
        background: getMarkerColor(row, this.props.colorMode),
        borderColor: '#ffffff',
        glyphColor: '#ffffff',
        scale: 0.9,
      });

      pin.element.style.pointerEvents = 'auto';

      const marker = new AdvancedMarkerElement({
        position,
        content: pin.element,
        gmpClickable: true,
      });

      marker.__row = row;

      this.bindDomEvents(marker.element || marker.content, () => this.openMarkerInfo(marker, row));
      marker.addListener('click', () => {
        this.cancelHoverClose();
        this.openMarkerInfo(marker, row);
      });

      return marker;
    });

    const { MarkerClusterer } = await import('@googlemaps/markerclusterer');

    if (!this.map) {
      return;
    }

    const renderer = {
      render: ({ count, position, markers }) => {
        const statusBreakdown = getClusterBreakdown(markers, 'status');
        const contentBreakdown = getClusterBreakdown(markers, 'content');
        const breakdown = this.props.colorMode === 'content' ? contentBreakdown : statusBreakdown;
        const content = createClusterContent({ count, breakdown });

        content.style.pointerEvents = 'auto';

        const clusterMarker = new AdvancedMarkerElement({
          position,
          content,
          zIndex: 1000 + count,
          gmpClickable: true,
        });

        clusterMarker.__clusterInfo = {
          count,
          statusBreakdown,
          contentBreakdown,
        };

        this.bindDomEvents(clusterMarker.element || clusterMarker.content, () =>
          this.openClusterInfo(clusterMarker),
        );
        clusterMarker.addListener('click', () => {
          this.cancelHoverClose();
          this.openClusterInfo(clusterMarker);
        });

        return clusterMarker;
      },
    };

    this.clusterer = new MarkerClusterer({
      map: this.map,
      markers: this.markers,
      renderer,
    });

    if (!this.hasFitBounds && this.markers.length) {
      this.ignoreNextIdle = true;
      this.map.fitBounds(bounds, 40);
      this.hasFitBounds = true;
    }
  };

  renderMarkerInfo = row => {
    if (this.props.renderMarkerInfo) {
      return this.props.renderMarkerInfo(row);
    }

    const titleText = escapeHtml(row.description || row.name || row.id);
    const rows = buildMarkerInfoRows(row);

    return `
      <div class="device-map-tooltip__title">${titleText}</div>
      <div class="device-map-tooltip__grid">
        ${rows
          .map(
            item =>
              `<div class="device-map-tooltip__label">${item.label}</div><div class="device-map-tooltip__value">${item.valueHtml || item.value}</div>`,
          )
          .join('')}
      </div>
    `;
  };

  renderClusterInfo = info => {
    if (this.props.renderClusterInfo) {
      return this.props.renderClusterInfo(info);
    }

    const { count, statusBreakdown, contentBreakdown } = info;
    const statusCounts = statusBreakdown?.counts || {};
    const contentCounts = contentBreakdown?.counts || {};
    const statusRows = STATUS_KEYS.filter(key => statusCounts[key])
      .map(key => `<div>${STATUS_LABELS[key]}: ${statusCounts[key]}</div>`)
      .join('');
    const contentRows = CONTENT_KEYS.filter(key => contentCounts[key])
      .map(key => `<div>${CONTENT_LABELS[key]}: ${contentCounts[key]}</div>`)
      .join('');

    return `
      <div class="device-map-tooltip__title">Cluster breakdown</div>
      <div>Total: ${count}</div>
      <div style="margin-top:6px;font-weight:600;color:#333;">Status</div>
      ${statusRows || '<div>Unknown: 0</div>'}
      <div style="margin-top:6px;font-weight:600;color:#333;">Content</div>
      ${contentRows || '<div>Unknown: 0</div>'}
    `;
  };

  openMarkerInfo = (marker, row) => {
    const html = this.renderMarkerInfo(row);
    const anchorEl = marker?.element || marker?.content || null;

    this.openOverlay(marker, html, anchorEl);
  };

  openClusterInfo = marker => {
    const info = marker.__clusterInfo;

    if (!info) {
      return;
    }

    const html = this.renderClusterInfo(info);
    const anchorEl = marker?.element || marker?.content || null;

    this.openOverlay(marker, html, anchorEl);
  };

  escapeHtml = value => escapeHtml(value);

  renderSummaryOverlay = () => {
    if (!this.props.showSummary) {
      return null;
    }

    const rows = this.getRows(this.props, this.state) || [];
    const totalRows = (this.state.totalRows || []).length ? this.state.totalRows : rows;
    const { onlineStatus, contentStatus } = getStatusCounts(rows);
    const totals = getStatusCounts(totalRows);

    return (
      <div className="map-summary">
        <div className="map-summary__title">Fleet summary</div>
        <table className="map-summary__table">
          <thead>
            <tr>
              <th />
              <th>Visible</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr className="map-summary__row">
              <td>Devices</td>
              <td>{rows.length}</td>
              <td>{totalRows.length}</td>
            </tr>
            <tr className="map-summary__section">
              <td colSpan="3">Online</td>
            </tr>
            <tr className="map-summary__row">
              <td>Online</td>
              <td>{onlineStatus.online}</td>
              <td>{totals.onlineStatus.online}</td>
            </tr>
            <tr className="map-summary__row">
              <td>Warning</td>
              <td>{onlineStatus.warning}</td>
              <td>{totals.onlineStatus.warning}</td>
            </tr>
            <tr className="map-summary__row">
              <td>Offline</td>
              <td>{onlineStatus.offline}</td>
              <td>{totals.onlineStatus.offline}</td>
            </tr>
            <tr className="map-summary__section">
              <td colSpan="3">Content</td>
            </tr>
            <tr className="map-summary__row">
              <td>Current</td>
              <td>{contentStatus.current}</td>
              <td>{totals.contentStatus.current}</td>
            </tr>
            <tr className="map-summary__row">
              <td>Downloading</td>
              <td>{contentStatus.downloading}</td>
              <td>{totals.contentStatus.downloading}</td>
            </tr>
            <tr className="map-summary__row">
              <td>Pending</td>
              <td>{contentStatus.pending}</td>
              <td>{totals.contentStatus.pending}</td>
            </tr>
            <tr className="map-summary__row">
              <td>Outdated</td>
              <td>{contentStatus.outdated}</td>
              <td>{totals.contentStatus.outdated}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  render() {
    const { mapError, mapReady, dataError } = this.state;
    const { className: propClassName } = this.props;
    const className = ['device-map', propClassName].filter(Boolean).join(' ');

    return (
      <div className={className}>
        {(mapError || dataError) && <Alert bsStyle="warning">{mapError || dataError}</Alert>}
        {!mapError && !mapReady && <Alert bsStyle="info">Loading map…</Alert>}
        <div className="map-canvas">
          {this.renderSummaryOverlay()}
          <div ref={this.mapRef} className="map-root" />
        </div>
      </div>
    );
  }
}

DeviceMapCanvas.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({})),
  points: PropTypes.arrayOf(PropTypes.shape({})),
  colorMode: PropTypes.oneOf(['status', 'content']).isRequired,
  className: PropTypes.string,
  onBoundsChange: PropTypes.func,
  onMapStatus: PropTypes.func,
  renderMarkerInfo: PropTypes.func,
  renderClusterInfo: PropTypes.func,
  dataUrl: PropTypes.string,
  pollIntervalMs: PropTypes.number,
  totalPollIntervalMs: PropTypes.number,
  pauseWhenHidden: PropTypes.bool,
  onData: PropTypes.func,
  onTotals: PropTypes.func,
  showSummary: PropTypes.bool,
};

DeviceMapCanvas.defaultProps = {
  rows: null,
  points: null,
  className: null,
  onBoundsChange: null,
  onMapStatus: null,
  renderMarkerInfo: null,
  renderClusterInfo: null,
  dataUrl: null,
  pollIntervalMs: 15000,
  totalPollIntervalMs: null,
  pauseWhenHidden: true,
  onData: null,
  onTotals: null,
  showSummary: false,
};
